import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { AppDataContext } from '~/contexts/AppDataContext';
import Button from '~/components/Shared/Buttons/Button';
import Separator from '~/components/Shared/Separator/Separator';
import { ArticleContext } from '~/contexts/ArticleContext';
import { getHasSlideshow } from '~/utilities/getHasSlideshow';
import { cva } from 'class-variance-authority';
import {
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { updatePageUrl } from '~/utilities/updatePageUrl';
import { GA } from '~/utilities/GA';
import { getCurrentPathname } from '~/utilities/getCurrentPathname';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';

const cssClasses = cva('w-fit', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

export default component$((): JSX.Element => {
  const { payloadToSSR, appState, themeComponents, env } =
    useContext(AppDataContext);
  const article = useContext(ArticleContext);

  if (!article) return <></>;

  const slideshow = payloadToSSR.pageContent.slideshow ?? article.slideshow;

  if (!getHasSlideshow(article as ArticleInterface, slideshow)) return <></>;

  const previousDisabled = appState.currentSlide <= 1;
  const nextDisabled =
    appState.currentSlide >= Object.keys(slideshow?.slidesLinks ?? {}).length;

  return (
    <div
      id='slideshow-controls'
      data-testid='slideshow-controls'
      class='flex gap-2.5 md:gap-5 items-center mb-5 scroll-mt-20'
    >
      <div class='button-wrapper'>
        <Button
          id='prev-button'
          testId='prev-button'
          ariaLabel='Previous Slide'
          variant={previousDisabled ? 'disabled' : 'primary'}
          disabled={previousDisabled}
          onClick$={() => {
            if (previousDisabled) return;

            appState.currentSlide--;
            updatePageUrl(
              getCurrentPathname(payloadToSSR),
              appState.currentSlide,
              env
            );

            const GoogleAnalytics = new GA(
              article as ArticleInterface,
              payloadToSSR,
              {
                currentSlide: appState.currentSlide,
              }
            );

            GoogleAnalytics.pushPageViewEvent({
              event: 'GTM data ready to GA - Slideshow',
              eventLabel: 'Slideshow page view',
              eventAction: 'Slideshow Pageview',
              isSlideshow: true,
              finiteScrollIndex: 0,
              moreProperties: {
                slideNumber: appState.currentSlide,
                slideControlsClick: 'prev',
              },
            });
          }}
        >
          {slideshow?.prevText.value}
        </Button>
      </div>

      <Separator color={'grey'} />

      {slideshow?.currentSlide !== null && (
        <div
          class={cssClasses({
            color: themeComponents.intro.introTextColor.value,
            ...getFontSizeObject(
              themeComponents.intro,
              'introTextSizeIn',
              'Screen'
            ),
          })}
        >
          {appState.currentSlide} {slideshow?.ofText.value}{' '}
          {Object.keys(slideshow?.slidesLinks ?? {}).length}
        </div>
      )}

      <Separator color={'grey'} />

      <div class='button-wrapper'>
        <Button
          id='next-button'
          testId='next-button'
          ariaLabel='Next Slide'
          variant={nextDisabled ? 'disabled' : 'primary'}
          disabled={nextDisabled}
          onClick$={() => {
            if (nextDisabled) return;

            appState.currentSlide++;
            updatePageUrl(
              getCurrentPathname(payloadToSSR),
              appState.currentSlide,
              env
            );

            const GoogleAnalytics = new GA(
              article as ArticleInterface,
              payloadToSSR,
              {
                currentSlide: appState.currentSlide,
              }
            );

            GoogleAnalytics.pushPageViewEvent({
              event: 'GTM data ready to GA - Slideshow',
              eventLabel: 'Slideshow page view',
              eventAction: 'Slideshow Pageview',
              isSlideshow: true,
              finiteScrollIndex: 0,
              moreProperties: {
                slideNumber: appState.currentSlide,
                slideControlsClick: 'next',
              },
            });
          }}
        >
          {slideshow?.nextText.value}
        </Button>
      </div>
    </div>
  );
});
